import apiClient from "./apiClient";
import publicApiClient from "./publicApiClient";
import { NETWORK_NAME, NetworkType } from "./network";
import { TokenSupport } from "types/tokenSupport";

const RECENT_TERMINALS_LIMIT = 10;
const RECENT_REDEEMS_LIMIT = 5;
const RECENT_REFUNDS_LIMIT = 5;

export async function getEmailByMerchantId(
  merchantId: string,
): Promise<string | null> {
  const res = await publicApiClient.post("/auth/get-email", { merchantId });
  return res.data ?? null;
}

export interface RegisterMerchantPayload {
  uid: string;
  email: string;
  merchantId: string;
  merchantDesc: string;
}

export interface PosByTreasury {
  serialId: string;
  serial: string;
  merchantId: string;
  status: number;
  statusLabel: "UNASSIGNED" | "IDLE" | "ACTIVE" | "BLOCKED";
  registeredAt: number;
  unassigned: boolean;
  vendorDigit: number;
  typeDigit: number;
  // merchantPosSeq: number;
  // fullPosId: string;
  // completeId: string;
}

export async function registerMerchant(payload: RegisterMerchantPayload) {
  return publicApiClient.post("/registerMerchant", payload);
}

// network is threaded in from the callers (React hooks that already read it, and
// the redux thunk which has getState) rather than pulled from a module-level
// global, so these stay pure and the required input is visible at every call site.
export async function getAppConfig(network: NetworkType) {
  return apiClient.get("/appConfig", {
    params: {
      network_name: NETWORK_NAME,
      network_type: network,
    },
  });
}

export async function getTokenSupport(
  token: string,
  network: NetworkType,
): Promise<TokenSupport> {
  const res = await apiClient.get("/token/support", {
    params: {
      token,
      network_name: NETWORK_NAME,
      network_type: network,
    },
  });
  return res.data;
}

export async function getTerminalsApi(
  merchantId: string,
  network: NetworkType,
) {
  return apiClient.get<PosByTreasury[]>("/recentPosByMerchant", {
    params: {
      merchantId,
      network_name: NETWORK_NAME,
      network_type: network,
      limit: RECENT_TERMINALS_LIMIT,
    },
  });
}

export async function lookupTerminalApi(
  merchantId: string,
  serial: string,
  network: NetworkType,
) {
  return apiClient.get<PosByTreasury>("/posBySerialForMerchant", {
    params: {
      merchantId,
      serial,
      network_name: NETWORK_NAME,
      network_type: network,
    },
  });
}

export async function getMerchant(merchantId: string, network: NetworkType) {
  return apiClient.get("/getMerchant", {
    params: {
      merchantId,
      network_name: NETWORK_NAME,
      network_type: network,
    },
  });
}

// `token` undefined => backend uses the contract's default. Axios drops undefined params.
export async function getMerchantTransactions(
  merchantAddress: string,
  network: NetworkType,
  token?: string,
) {
  return apiClient.get("/getMerchantTransactions", {
    params: {
      merchantAddress,
      network_name: NETWORK_NAME,
      network_type: network,
      token,
    },
  });
}

export async function getMerchantBalance(
  merchantAddress: string,
  network: NetworkType,
  token?: string,
) {
  return apiClient.get("/getMerchantBalance", {
    params: {
      merchantAddress,
      network_name: NETWORK_NAME,
      network_type: network,
      token,
    },
  });
}

export async function getCardNumberFromAddress(
  address: string,
  network: NetworkType,
) {
  return apiClient.get("/getCardNumberFromAddress", {
    params: {
      address,
      network_name: NETWORK_NAME,
      network_type: network,
    },
  });
}

export async function createRedeemRequest(
  payload: {
    merchantId: string;
    treasuryId: string;
    amountInVip: number;
    settlementToken?: string;
  },
  network: NetworkType,
) {
  return apiClient.post("/redeem", {
    ...payload,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}

// `token` filters to one settlement token; omitted returns EVERY token (the backend keeps that
// additive). Axios drops undefined params, so nothing appears on the wire when it is absent.
export async function getRedeemRequests(
  merchantId: string,
  network: NetworkType,
  token?: string,
) {
  return apiClient.get("/redeem/recent", {
    params: {
      merchantId,
      network_name: NETWORK_NAME,
      network_type: network,
      limit: RECENT_REDEEMS_LIMIT,
      activeOnly: 1,
      token,
    },
  });
}

export async function getMoneySettlementAccountStatus(merchantId: string) {
  return apiClient.get("/moneySettlement/account-status", {
    params: { merchantId },
  });
}

// Unified merchant onboarding payload. Discriminated by platformId: Stripe needs
// only country + businessType (KYC is hosted); Column also carries the bank
// counterparty details. The backend routes by platformId and maps businessType
// to the provider's shape (Stripe business_type / Column legal_type).
export type OnboardMerchantPayload = {
  counterparty: {
    name: string;
    routingNumber: string;
    accountNumber: string;
    accountType: string;
    legalId?: string;
    address: {
      line_1: string;
      city: string;
      state?: string;
      postal_code?: string;
      country_code: string;
    };
  };
};

// merchantId names WHICH merchant is being onboarded — an account may hold several, each with
// its own settlement account. The backend scope-checks it against the session.
export async function onboardMerchant(
  merchantId: string,
  payload?: OnboardMerchantPayload,
) {
  return apiClient.post("/moneySettlement/onboard", { ...payload, merchantId });
}

export async function getStripeDashboardLink(merchantId: string) {
  return apiClient.get("/stripe/dashboard-link", { params: { merchantId } });
}

export async function getPendingRefunds(
  merchantId: string,
  network: NetworkType,
  token?: string,
) {
  return apiClient.get(`/refund/recent`, {
    params: {
      merchantId,
      network_name: NETWORK_NAME,
      network_type: network,
      limit: RECENT_REFUNDS_LIMIT,
      token,
    },
  });
}

export async function settleReturn(
  payload: {
    requestId: string;
    merchantId: string;
    cardNumber: string;
  },
  network: NetworkType,
) {
  return apiClient.post("/refund/approve", {
    ...payload,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}

export async function rejectReturn(
  payload: {
    requestId: string;
    merchantId: string;
    cardNumber: string;
    reason: number;
  },
  network: NetworkType,
) {
  return apiClient.post("/refund/reject", {
    ...payload,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}
